import { EContractStatus } from "api/enums";
import { EContractsSortField } from "api/enums/EContractsSortField";
import { makeAutoObservable } from "mobx";
import { compareStrings, enumHasValue } from "utils";
import ContractModel from "../api/models/ContractModel";
import ContractListStore from "./Contract/ContractListStore";
import UserStore from "./UserStore";

class Store {
  public filteredContracts: ContractModel[] = [];
  public filterSearch: string = "";
  public sortField: string = "";
  public filterStatus: { [status: string]: boolean } = {};

  constructor() {
    makeAutoObservable(this);
    this.reset();
  }

  reset() {
    this.filterSearch = "";
    this.sortField = "";
    this.filterStatus = this.initializeFilterStatus();
  }

  public setSortField(value: EContractsSortField) {
    this.sortField = value;
  }

  public setFilterStatus(status: string, checked: boolean) {
    this.filterStatus[status] = checked;
  }

  public async setAndSaveFilterStatus(status: string, checked: boolean) {
    if (!enumHasValue(EContractStatus, status)) return;
    this.setFilterStatus(status, checked);

    const statusList = Object.keys(this.filterStatus).filter(
      (stt) => this.filterStatus[stt]
    );
    await UserStore.saveContractStatusFiltered(statusList);
  }

  public setStatusFilterWithUserConfig() {
    const user = UserStore.currentUser;
    if (!user) return;

    const status = user.contractStatusFiltered;
    if (!status || !status.length) return;

    Object.keys(this.filterStatus).forEach(
      (stt) => (this.filterStatus[stt] = status.includes(stt))
    );
  }

  public refreshFilteredContracts() {
    this.filteredContracts = ContractListStore.contracts
      .filter((contract) => KanbanStore.applyFilters(contract))
      .sort((a, b) => this.applySort(a, b));
  }

  private applyFilters(data: ContractModel): boolean {
    return (
      KanbanStore.applyStatusFilter(data) && KanbanStore.applySearchFilter(data)
    );
  }

  private applySearchFilter(data: ContractModel): boolean {
    if (!this.filterSearch) {
      return true;
    }

    const search = this.filterSearch.toLowerCase();
    const valueIsSelected = (text = "") =>
      text.toLowerCase().indexOf(search) !== -1;

    return (
      valueIsSelected(data.name) ||
      valueIsSelected(data.development.name) ||
      valueIsSelected(data.development.customer) ||
      valueIsSelected(data.development.local)
    );
  }

  private applyStatusFilter(data: ContractModel) {
    if (!this.filterStatus) {
      return true;
    }

    return this.filterStatus[data.status] || false;
  }

  private applySort(a: ContractModel, b: ContractModel): number {
    switch (this.sortField) {
      case EContractsSortField.PROJECT:
        return compareStrings(a.development.name, b.development.name);
      case EContractsSortField.CREATED_DATE:
        return a.createdAt.getTime() - b.createdAt.getTime();
      case EContractsSortField.TOTAL:
        return a.total - b.total;
      case EContractsSortField.PERCENTUAL:
        return a.getBillingPercentual() - b.getBillingPercentual();
      default:
        return this.sortByStatus(a, b);
    }
  }

  private sortByStatus(a: ContractModel, b: ContractModel): number {
    const orderStatus = {
      [EContractStatus.AGUARDANDO]: 0,
      [EContractStatus.ANDAMENTO]: 1,
      [EContractStatus.PARALISADO]: 2,
      [EContractStatus.CONCLUIDO]: 3,
      [EContractStatus.ARQUIVADO]: 4,
    };

    return orderStatus[a.status] - orderStatus[b.status];
  }

  private initializeFilterStatus() {
    const obj = {};
    Object.values(EContractStatus).forEach(
      (stt) => (obj[stt] = stt !== EContractStatus.ARQUIVADO)
    );
    return obj;
  }
}

const KanbanStore = new Store();
export default KanbanStore;
